import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import { SuppressedCell } from '@/components/admin/SuppressedCell';
import { handleHashLinkClick } from '@/lib/hashNav';
import { ArrowRight, ArrowUpRight, Check, Menu } from 'lucide-react';
import ivLogomark from '@/assets/iv-talent-logomark.png';
import valueGridBg from '@/assets/value-grid-bg.jpg';

/* -------------------------------------------------------------- */
/*  Reveal                                                        */
/* -------------------------------------------------------------- */

function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Nav                                                           */
/* -------------------------------------------------------------- */

import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group shrink-0">
      <img src={ivLogomark} alt="IV Talent" className="hidden sm:block h-8 w-8 rounded-md" />
      <span className="font-display text-[20px] tracking-tight whitespace-nowrap text-ink">
        CultureLens
      </span>
    </Link>
  );
}





/* -------------------------------------------------------------- */
/*  Product artifacts                                             */
/* -------------------------------------------------------------- */

function HeroInsightCard() {
  // Believable surfaced-theme card, static.
  const sparkPoints = [42, 44, 41, 46, 49, 47, 52, 55, 53, 58, 61, 64];
  const max = Math.max(...sparkPoints);
  const min = Math.min(...sparkPoints);
  const w = 220;
  const h = 44;
  const path = sparkPoints
    .map((v, i) => {
      const x = (i / (sparkPoints.length - 1)) * w;
      const y = h - ((v - min) / (max - min)) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <div className="paper-card rounded-[4px] p-6 md:p-7 w-full max-w-[440px] overflow-hidden">
      <div className="flex items-center justify-between">
        <div className="font-grotesk text-[12px] text-charcoal/60">
          Insight board · Engineering
        </div>
        <div className="font-grotesk text-[12px] tnum text-charcoal/60">
          Week 14
        </div>
      </div>

      <h3 className="mt-5 font-display text-[20px] md:text-[22px] leading-[1.25] tracking-[-0.01em] text-ink max-w-[28ch]">
        Conflicting priorities mentioned in 38% of conversations.
      </h3>

      <div className="mt-6 flex items-end justify-between gap-6">
        <div className="min-w-0">
          <div className="font-display tnum text-[40px] leading-none text-ink">
            38%
          </div>
          <div className="mt-2 font-grotesk text-[12px] text-charcoal/60">
            <span className="tnum">+6 pts</span> vs. prior 4 weeks
          </div>
        </div>
        <div className="w-full max-w-[220px] min-w-0 shrink">
          <svg
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="xMidYMid meet"
            className="block w-full h-auto overflow-hidden"
            aria-hidden="true"
          >
            <path
              d={sparkPoints
                .map((v, i) => {
                  const x = 3 + (i / (sparkPoints.length - 1)) * (w - 6);
                  const y = 3 + ((h - 6) - ((v - min) / (max - min)) * (h - 6));
                  return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
                })
                .join(' ')}
              fill="none"
              stroke="hsl(var(--brand))"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            {(() => {
              const i = sparkPoints.length - 1;
              const v = sparkPoints[i];
              const x = 3 + (i / (sparkPoints.length - 1)) * (w - 6);
              const y = 3 + ((h - 6) - ((v - min) / (max - min)) * (h - 6));
              return <circle cx={x} cy={y} r="2.5" fill="hsl(var(--brand))" />;
            })()}
          </svg>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-hairline grid grid-cols-2 gap-x-6 gap-y-2 font-grotesk text-[12px]">
        <div className="flex items-center justify-between">
          <span className="text-charcoal/60">Engineering</span>
          <span className="tnum text-ink">52%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-charcoal/60">Operations</span>
          <span className="tnum text-ink">31%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-charcoal/60">Product</span>
          <span className="tnum text-ink">24%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-charcoal/60">Org-wide</span>
          <span className="tnum text-ink">38%</span>
        </div>
      </div>

      <div className="mt-5 font-grotesk text-[11px] text-charcoal/55">
        Aggregated across 4 teams · privacy floor k ≥ 5 enforced
      </div>
    </div>
  );
}

function TrustSchematic() {
  // Four nodes: voice → salted hash → cohort → admin view. Individual layer sealed.
  return (
    <svg
      viewBox="0 0 360 200"
      className="w-full h-auto"
      role="img"
      aria-label="Identity isolation: voice, salted hash, aggregated cohort, admin view"
    >
      {/* connector rule */}
      <line x1="40" y1="100" x2="320" y2="100" stroke="hsl(var(--hairline))" strokeWidth="1" />

      {/* sealed individual layer */}
      <g transform="translate(20,70)">
        <rect width="60" height="60" rx="2" fill="hsl(var(--cream-warm))" stroke="hsl(var(--ink))" strokeWidth="1" />
        <rect x="6" y="6" width="48" height="48" rx="1" fill="none" stroke="hsl(var(--ink))" strokeDasharray="2 2" opacity="0.45" />
        <text x="30" y="34" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--ink))">voice</text>
        <text x="30" y="46" textAnchor="middle" fontSize="7" fontFamily="Inter, sans-serif" fill="hsl(var(--charcoal))" opacity="0.7">private</text>
      </g>
      <text x="50" y="155" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--charcoal))" opacity="0.7">Individual</text>

      {/* salted hash */}
      <g transform="translate(115,80)">
        <rect width="60" height="40" rx="2" fill="none" stroke="hsl(var(--hairline))" strokeWidth="1" />
        <text x="30" y="24" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--ink))">salted hash</text>
      </g>
      <text x="145" y="155" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--charcoal))" opacity="0.7">Per-org isolation</text>

      {/* cohort */}
      <g transform="translate(210,80)">
        <rect width="60" height="40" rx="2" fill="none" stroke="hsl(var(--hairline))" strokeWidth="1" />
        <text x="30" y="20" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--ink))">cohort</text>
        <text x="30" y="32" textAnchor="middle" fontSize="8" fontFamily="Inter, sans-serif" fill="hsl(var(--charcoal))" opacity="0.7">k ≥ 5</text>
      </g>
      <text x="240" y="155" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--charcoal))" opacity="0.7">Aggregated</text>

      {/* admin view */}
      <g transform="translate(295,80)">
        <rect width="50" height="40" rx="2" fill="hsl(var(--ink))" />
        <text x="25" y="24" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--cream))">admin</text>
      </g>
      <text x="320" y="155" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill="hsl(var(--charcoal))" opacity="0.7">Read-only</text>
    </svg>
  );
}

function ThemesOverTime() {
  // Multi-line themes chart, restrained, one highlighted.
  const weeks = 12;
  const series = [
    { name: 'Psychological safety', highlight: true, values: [48, 49, 47, 50, 51, 53, 55, 57, 58, 60, 62, 64] },
    { name: 'Workload sustainability', values: [62, 60, 61, 59, 58, 57, 56, 55, 54, 53, 52, 51] },
    { name: 'Cross-team clarity', values: [44, 45, 43, 44, 46, 45, 44, 43, 42, 43, 41, 40] },
    { name: 'Recognition', values: [38, 39, 40, 40, 41, 42, 42, 43, 44, 44, 45, 46] },
  ];
  const W = 880;
  const H = 280;
  const padL = 44;
  const padR = 24;
  const padT = 24;
  const padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const yMin = 30;
  const yMax = 70;

  const xFor = (i: number) => padL + (i / (weeks - 1)) * innerW;
  const yFor = (v: number) => padT + (1 - (v - yMin) / (yMax - yMin)) * innerH;

  const pathFor = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`).join(' ');

  return (
    <div className="paper-card rounded-[4px] p-6 md:p-8 overflow-hidden">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-grotesk text-[12px] text-charcoal/60">
            Themes · org-wide · 12 weeks
          </div>
          <h3 className="mt-1 font-display text-[20px] md:text-[22px] leading-[1.25] tracking-[-0.01em] text-ink">
            Themes over time
          </h3>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 font-grotesk text-[12px]">
          {series.map((s) => (
            <div key={s.name} className="flex items-center gap-2">
              <span
                className="inline-block h-[2px] w-5"
                style={{
                  backgroundColor: s.highlight ? 'hsl(var(--brand))' : 'hsl(var(--charcoal) / 0.35)',
                }}
              />
              <span className={s.highlight ? 'text-ink' : 'text-charcoal/65'}>{s.name}</span>
            </div>
          ))}
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Themes over time chart">
        {/* y gridlines */}
        {[30, 40, 50, 60, 70].map((v) => (
          <g key={v}>
            <line
              x1={padL}
              x2={W - padR}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke="hsl(var(--hairline))"
              strokeWidth="1"
            />
            <text
              x={padL - 10}
              y={yFor(v) + 3}
              textAnchor="end"
              fontSize="10"
              fontFamily="Inter, sans-serif"
              fill="hsl(var(--charcoal))"
              opacity="0.55"
            >
              {v}%
            </text>
          </g>
        ))}
        {/* x labels */}
        {Array.from({ length: weeks }).map((_, i) => {
          if (i % 2 !== 0) return null;
          return (
            <text
              key={i}
              x={xFor(i)}
              y={H - padB + 16}
              textAnchor="middle"
              fontSize="10"
              fontFamily="Inter, sans-serif"
              fill="hsl(var(--charcoal))"
              opacity="0.55"
            >
              W{i + 1}
            </text>
          );
        })}
        {/* non-highlighted lines */}
        {series
          .filter((s) => !s.highlight)
          .map((s) => (
            <path
              key={s.name}
              d={pathFor(s.values)}
              fill="none"
              stroke="hsl(var(--charcoal))"
              strokeOpacity="0.32"
              strokeWidth="1.25"
            />
          ))}
        {/* highlighted line */}
        {series
          .filter((s) => s.highlight)
          .map((s) => (
            <g key={s.name}>
              <path d={pathFor(s.values)} fill="none" stroke="hsl(var(--brand))" strokeWidth="1.75" />
              {s.values.map((v, i) =>
                i === s.values.length - 1 ? (
                  <circle key={i} cx={xFor(i)} cy={yFor(v)} r="3" fill="hsl(var(--brand))" />
                ) : null,
              )}
            </g>
          ))}

        {/* annotation */}
        <g>
          <line
            x1={xFor(weeks - 1) - 60}
            y1={yFor(64) - 26}
            x2={xFor(weeks - 1) - 8}
            y2={yFor(64) - 6}
            stroke="hsl(var(--ink))"
            strokeOpacity="0.4"
            strokeWidth="0.75"
          />
          <text
            x={xFor(weeks - 1) - 64}
            y={yFor(64) - 32}
            textAnchor="end"
            fontSize="10.5"
            fontFamily="Inter, sans-serif"
            fill="hsl(var(--ink))"
          >
            Rising in Engineering, flat org-wide
          </text>
        </g>
      </svg>

      <div className="mt-5 font-grotesk text-[12px] text-charcoal/55">
        Themes are derived from confidential conversations. Each point represents ≥ 5 voices.
      </div>
    </div>
  );
}

function GapMap() {
  // Dumbbell: stated (charcoal dot) vs lived (brand dot), one highlighted.
  const rows = [
    { label: 'Open challenge', stated: 78, lived: 54, highlight: true },
    { label: 'Decision clarity', stated: 72, lived: 58 },
    { label: 'Ownership', stated: 80, lived: 71 },
    { label: 'Recognition', stated: 70, lived: 52 },
    { label: 'Career progression', stated: 65, lived: 49 },
    { label: 'Cross-team trust', stated: 74, lived: 63 },
  ];
  const W = 560;
  const H = 32; // per row
  const padL = 150;
  const padR = 24;
  const innerW = W - padL - padR;
  const xFor = (v: number) => padL + (v / 100) * innerW;

  return (
    <div className="paper-card rounded-[4px] p-6 md:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-grotesk text-[12px] text-charcoal/60">
            Gap map · stated vs lived
          </div>
          <h3 className="mt-1 font-display text-[20px] md:text-[22px] leading-[1.25] tracking-[-0.01em] text-ink">
            Where intent and experience diverge
          </h3>
        </div>
        <div className="flex gap-5 font-grotesk text-[12px]">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-charcoal/55" />
            <span className="text-charcoal/70">Stated</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-brand" />
            <span className="text-charcoal/70">Lived</span>
          </div>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H * rows.length + 20}`} className="w-full h-auto" role="img" aria-label="Stated versus lived gap map">
        {[0, 25, 50, 75, 100].map((v) => (
          <line
            key={v}
            x1={xFor(v)}
            x2={xFor(v)}
            y1={0}
            y2={H * rows.length}
            stroke="hsl(var(--hairline))"
            strokeWidth="1"
          />
        ))}
        {rows.map((r, i) => {
          const y = i * H + H / 2;
          return (
            <g key={r.label}>
              <text
                x={padL - 12}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fontFamily="Inter, sans-serif"
                fill={r.highlight ? 'hsl(var(--ink))' : 'hsl(var(--charcoal))'}
                opacity={r.highlight ? 1 : 0.75}
              >
                {r.label}
              </text>
              <line
                x1={xFor(Math.min(r.stated, r.lived))}
                x2={xFor(Math.max(r.stated, r.lived))}
                y1={y}
                y2={y}
                stroke={r.highlight ? 'hsl(var(--ink))' : 'hsl(var(--charcoal))'}
                strokeOpacity={r.highlight ? 0.6 : 0.3}
                strokeWidth={r.highlight ? 2 : 1.5}
              />
              <circle cx={xFor(r.stated)} cy={y} r={r.highlight ? 5 : 4} fill="hsl(var(--charcoal))" fillOpacity={r.highlight ? 0.85 : 0.55} />
              <circle cx={xFor(r.lived)} cy={y} r={r.highlight ? 5 : 4} fill="hsl(var(--brand))" />
              <text
                x={W - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="10.5"
                fontFamily="Inter, sans-serif"
                fill="hsl(var(--charcoal))"
                opacity="0.7"
              >
                {Math.abs(r.stated - r.lived)} pt gap
              </text>
            </g>
          );
        })}
        {/* axis labels */}
        {[0, 50, 100].map((v) => (
          <text
            key={v}
            x={xFor(v)}
            y={H * rows.length + 14}
            textAnchor="middle"
            fontSize="9.5"
            fontFamily="Inter, sans-serif"
            fill="hsl(var(--charcoal))"
            opacity="0.55"
          >
            {v}
          </text>
        ))}
      </svg>

      <div className="mt-5 font-grotesk text-[12px] text-charcoal/55">
        Stated drawn from the org&rsquo;s own culture brief. Lived derived from confidential conversation themes.
      </div>
    </div>
  );
}

function PrivacyEnforcementPanel() {
  return (
    <div className="paper-card-warm rounded-[4px] p-6">
      <div className="font-grotesk text-[12px] text-charcoal/60">
        Cohort view · Operations
      </div>
      <h4 className="mt-1 font-display text-[18px] leading-[1.3] tracking-[-0.01em] text-ink">
        Recognition · tenure split
      </h4>

      <div className="mt-5 divide-y divide-hairline border-y border-hairline">
        {[
          { label: '0-1 yrs', n: 14, value: '46%' },
          { label: '1-3 yrs', n: 22, value: '52%' },
          { label: '3-5 yrs', n: 9, value: '58%' },
        ].map((r) => (
          <div key={r.label} className="flex items-center justify-between py-2.5 font-grotesk text-[13px]">
            <span className="text-charcoal/75">{r.label}</span>
            <span className="text-charcoal/55 tnum">n = {r.n}</span>
            <span className="text-ink tnum">{r.value}</span>
          </div>
        ))}
      </div>

      <SuppressedCell count={2} />

      <div className="mt-4 font-grotesk text-[11px] text-charcoal/55 leading-relaxed">
        Privacy floor (k ≥ 5) is enforced at the data layer. Smaller cohorts never resolve to a number.
      </div>
    </div>
  );
}

function OperationalFlow() {
  const nodes = [
    {
      label: 'Inputs',
      caption: 'Your stated culture, in-scope challenges, segmentation choices.',
    },
    {
      label: 'Conversation layer',
      caption: 'One-to-one confidential conversations, voice or text, on the employee&rsquo;s schedule.',
    },
    {
      label: 'Privacy layer',
      caption: 'Identity isolation, per-org salted hashing, k ≥ 5 aggregation floor.',
      emphasised: true,
    },
    {
      label: 'Insight layer',
      caption: 'Themes, trends, and stated-vs-lived gaps across teams and time.',
    },
    {
      label: 'Action layer',
      caption: 'Decision-ready briefings for executives and team leads.',
    },
  ];

  return (
    <div className="w-full">
      {/* Desktop horizontal */}
      <div className="hidden md:block">
        <div className="grid grid-cols-5 gap-0">
          {nodes.map((n, i) => (
            <div key={n.label} className="relative">
              {i > 0 && (
                <div
                  className="absolute top-[18px] -left-1/2 right-1/2 h-px bg-hairline"
                  aria-hidden="true"
                />
              )}
              <div className="flex flex-col items-start">
                <div
                  className={`relative z-10 h-9 w-9 rounded-full border ${
                    n.emphasised
                      ? 'border-ink bg-ink text-cream'
                      : 'border-iv-orange/30 bg-iv-orange/10 text-ink'
                  } flex items-center justify-center font-grotesk text-[12px] tnum`}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div
                  className={`mt-5 font-display text-[16px] leading-[1.25] tracking-[-0.01em] text-ink ${
                    n.emphasised ? '' : ''
                  }`}
                >
                  {n.label}
                </div>
                <p
                  className="mt-2 font-grotesk text-[13px] text-charcoal/70 leading-relaxed pr-6 max-w-[28ch]"
                  dangerouslySetInnerHTML={{ __html: n.caption }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile vertical */}
      <div className="md:hidden space-y-8">
        {nodes.map((n, i) => (
          <div key={n.label} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div
                className={`h-8 w-8 rounded-full border flex items-center justify-center font-grotesk text-[11px] tnum ${
                  n.emphasised ? 'border-ink bg-ink text-cream' : 'border-iv-orange/30 bg-iv-orange/10 text-ink'
                }`}
              >
                {String(i + 1).padStart(2, '0')}
              </div>
              {i < nodes.length - 1 && <div className="w-px flex-1 bg-hairline mt-2" />}
            </div>
            <div className="pb-2">
              <div className="font-display text-[16px] leading-[1.25] tracking-[-0.01em] text-ink">
                {n.label}
              </div>
              <p
                className="mt-1 font-grotesk text-[13px] text-charcoal/70 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: n.caption }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Sections                                                      */
/* -------------------------------------------------------------- */

function Hero() {
  return (
    <section className="relative pt-32 pb-16 md:pt-36 md:pb-24 hero-gradient border-b border-hairline">
      <div className="relative max-w-content mx-auto px-6 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <div className="md:col-span-7">
          <Reveal>
            <div className="inline-flex items-center gap-2 font-grotesk text-[12px] tracking-[0.16em] uppercase text-charcoal/65">
              <span className="h-1.5 w-1.5 rounded-full bg-ink" />
              Culture insights platform
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-5 font-display text-[44px] sm:text-[56px] md:text-[64px] lg:text-[72px] leading-[1.02] tracking-[-0.025em] text-ink max-w-[16ch]">
              See the culture
              <span className="text-charcoal/55"> you actually have.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 max-w-xl text-[17px] md:text-[18px] text-charcoal/80 font-grotesk leading-[1.6]">
              CultureLens turns confidential conversations across your teams into clear
              culture insight. You see which themes are gaining ground, where stated and
              lived culture diverge, and where leaders should look next.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <GetStartedDialog>
                <Button className="group bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px] font-medium hover:-translate-y-px transition-transform duration-300">
                  Request access <ArrowRight className="ml-2 h-4 w-4 cta-arrow" />
                </Button>
              </GetStartedDialog>
              <Link
                to="/auth/signin"
                className="font-grotesk text-[14px] text-charcoal hover:text-ink transition-colors inline-flex items-center gap-1"
              >
                Sign in <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/who-we-are#who-its-for"
                onClick={handleHashLinkClick('/who-we-are', '#who-its-for')}
                className="font-grotesk text-[14px] text-charcoal hover:text-ink transition-colors inline-flex items-center gap-1 underline underline-offset-4"
              >
                See who CultureLens is for
              </Link>
            </div>
          </Reveal>
          <Reveal delay={300}>
            <ul className="mt-10 pt-8 border-t border-hairline grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-8 font-grotesk text-[13px] text-charcoal/70">
              <li>
                <div className="text-ink font-medium">Themes</div>
                <div className="mt-1 text-charcoal/60 leading-relaxed">
                  What&rsquo;s rising, week over week.
                </div>
              </li>
              <li>
                <div className="text-ink font-medium">Stated vs lived</div>
                <div className="mt-1 text-charcoal/60 leading-relaxed">
                  Where the culture story breaks down.
                </div>
              </li>
              <li>
                <div className="text-ink font-medium">Leader briefings</div>
                <div className="mt-1 text-charcoal/60 leading-relaxed">
                  Written from real employee voices.
                </div>
              </li>
            </ul>
          </Reveal>
        </div>

        <div className="md:col-span-5">
          <Reveal delay={160}>
            <HeroInsightCard />
          </Reveal>
        </div>
      </div>
    </section>
  );
}


function TrustArchitecture() {
  const pillars = [
    {
      title: 'Confidential by construction',
      body:
        'Individual conversations are not visible to anyone in the organization, including admins. Privacy is built into how the data is stored, not just promised in a policy.',
    },
    {
      title: 'k ≥ 5 aggregation floor',
      body:
        'No view ever resolves to fewer than five people. Smaller cohorts are suppressed at query time, not on the client.',
    },
    {
      title: 'Admin-safe reporting',
      body:
        'Identity is structurally isolated from content using per-organization salted hashing. Admin reads run through verified, audited server-side controls.',
    },
  ];

  return (
    <section id="trust" className="bg-cream py-20 md:py-28 border-b border-hairline">
      <div className="max-w-content mx-auto px-6 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <Reveal>
            <h2 className="font-display text-[32px] md:text-[44px] text-ink leading-[1.08] tracking-[-0.02em] max-w-[20ch]">
              Trust is the architecture, not the disclaimer.
            </h2>
            <p className="mt-5 font-grotesk text-charcoal/75 text-[17px] leading-[1.6] max-w-[58ch]">
              People only speak honestly inside systems they can inspect for themselves.
              CultureLens is built so confidentiality is enforced by the system itself,
              not by a promise.
            </p>
          </Reveal>

          <div className="mt-12 divide-y divide-hairline border-y border-hairline">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="py-6 grid md:grid-cols-[1fr_2fr] gap-4 md:gap-10">
                  <h3 className="font-display text-[18px] md:text-[20px] text-ink leading-[1.3] tracking-[-0.01em]">
                    {p.title}
                  </h3>
                  <p className="font-grotesk text-charcoal/75 text-[15px] leading-relaxed max-w-[58ch]">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="md:col-span-5 md:pl-8">
          <Reveal delay={120}>
            <div className="paper-card-warm rounded-[4px] p-6 md:p-7">
              <div className="font-grotesk text-[12px] text-charcoal/60 mb-4">
                Identity isolation
              </div>
              <TrustSchematic />
              <div className="mt-4 font-grotesk text-[11px] text-charcoal/55 leading-relaxed">
                Voice never leaves the individual layer. Admin views read only from the cohort layer.
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ProductProofOne() {
  return (
    <section id="product" className="bg-cream-warm py-20 md:py-28">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <h2 className="font-display text-[32px] md:text-[44px] text-ink leading-[1.08] tracking-[-0.02em] max-w-[24ch]">
            Themes you can track, not numbers you have to defend.
          </h2>
          <p className="mt-5 font-grotesk text-charcoal/75 text-[17px] leading-[1.6] max-w-[60ch]">
            Conversations are grouped into a small set of themes and tracked week over
            week. You see what is moving, where, and at what scale. You never see an
            individual response.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-12">
            <ThemesOverTime />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ProductProofTwo() {
  return (
    <section className="bg-cream py-20 md:py-28 border-y border-hairline">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <h2 className="font-display text-[32px] md:text-[44px] text-ink leading-[1.08] tracking-[-0.02em] max-w-[24ch]">
            Where your stated culture and your lived culture come apart.
          </h2>
          <p className="mt-5 font-grotesk text-charcoal/75 text-[17px] leading-[1.6] max-w-[60ch]">
            The gap map places each stated value next to what people actually describe in
            conversations. Privacy enforcement sits in the same view, not on a separate
            page.
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-12 gap-8">
          <Reveal delay={120} className="md:col-span-8">
            <GapMap />
          </Reveal>
          <Reveal delay={200} className="md:col-span-4">
            <PrivacyEnforcementPanel />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ValueGrid() {
  return (
    <section className="relative bg-cream-warm py-20 md:py-28 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${valueGridBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(24px)',
          opacity: 0.18,
          transform: 'scale(1.08)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none bg-gradient-to-b from-cream-warm/85 via-cream-warm/75 to-cream-warm/95"
      />

      <div className="relative max-w-content mx-auto px-6">
        <div className="grid md:grid-cols-12 md:gap-x-12">
          <Reveal className="md:col-span-8">
            <div className="font-grotesk text-[12px] tracking-[0.16em] uppercase text-charcoal/60">
              What leaders actually get
            </div>
            <h3 className="mt-4 font-display text-[28px] md:text-[40px] text-ink leading-[1.08] tracking-[-0.02em] max-w-[22ch]">
              Insight you can act on. In your people&rsquo;s own words.
            </h3>
            <p className="mt-6 font-grotesk text-charcoal/80 text-[17px] leading-[1.6] max-w-[60ch]">
              Multiple choice cannot represent how work actually feels. Confidential
              conversations can. Each one adapts to the person, follows the thread, and
              produces real employee voices. The platform turns that into themes, trends,
              and briefings your leadership team can act on.
            </p>

            <figure className="mt-8 max-w-[60ch] border-l-2 border-ink/20 pl-5">
              <blockquote className="font-display text-[18px] md:text-[20px] text-ink/85 leading-[1.45] italic">
                &ldquo;The work I&rsquo;m proudest of is the work nobody asked me to do.&rdquo;
              </blockquote>
              <figcaption className="mt-2 font-grotesk text-[12px] uppercase tracking-[0.14em] text-charcoal/50">
                Anonymized response &middot; Senior engineer
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="mt-20 md:mt-24 grid md:grid-cols-3 md:gap-x-12 gap-y-12 border-t border-hairline pt-12">
          <Reveal delay={120}>
            <h3 className="font-display text-[22px] md:text-[24px] text-ink leading-[1.25] tracking-[-0.01em] max-w-[22ch]">
              Stated vs lived.
            </h3>
            <p className="mt-4 font-grotesk text-charcoal/75 text-[15px] leading-[1.6]">
              Compare the culture you describe in your own words to what employees
              describe in theirs. That gap is where the work is.
            </p>
            <figure className="mt-6 border-l-2 border-ink/15 pl-4">
              <blockquote className="font-display text-[15px] md:text-[16px] text-ink/80 leading-[1.5] italic">
                &ldquo;On paper we&rsquo;re collaborative. In practice, decisions
                land in my inbox after they&rsquo;ve already been made.&rdquo;
              </blockquote>
              <figcaption className="mt-2 font-grotesk text-[11px] uppercase tracking-[0.14em] text-charcoal/50">
                Anonymized response &middot; Product manager
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={200}>
            <h3 className="font-display text-[22px] md:text-[24px] text-ink leading-[1.25] tracking-[-0.01em] max-w-[22ch]">
              Weekly, not annual.
            </h3>
            <p className="mt-4 font-grotesk text-charcoal/75 text-[15px] leading-[1.6]">
              A live picture of how your organisation is running, so small shifts
              surface before they become incidents.
            </p>
            <figure className="mt-6 border-l-2 border-ink/15 pl-4">
              <blockquote className="font-display text-[15px] md:text-[16px] text-ink/80 leading-[1.5] italic">
                &ldquo;By the third week, we could see the same thing being
                said in three different teams. That was the moment we acted.&rdquo;
              </blockquote>
              <figcaption className="mt-2 font-grotesk text-[11px] uppercase tracking-[0.14em] text-charcoal/50">
                Cohort member &middot; COO, professional services
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={280}>
            <h3 className="font-display text-[22px] md:text-[24px] text-ink leading-[1.25] tracking-[-0.01em] max-w-[22ch]">
              Decision-ready for leaders.
            </h3>
            <p className="mt-4 font-grotesk text-charcoal/75 text-[15px] leading-[1.6]">
              Briefings written for executives and team leads, grounded in real
              voices and weekly trends. Aggregated, never identified.
            </p>
            <figure className="mt-6 border-l-2 border-ink/15 pl-4">
              <blockquote className="font-display text-[15px] md:text-[16px] text-ink/80 leading-[1.5] italic">
                &ldquo;For the first time, I walked into the exec meeting
                already knowing what my team would say if they were in the room.&rdquo;
              </blockquote>
              <figcaption className="mt-2 font-grotesk text-[11px] uppercase tracking-[0.14em] text-charcoal/50">
                Cohort member &middot; Head of People, 600-person firm
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function System() {
  return (
    <section id="system" className="bg-cream py-20 md:py-28 border-t border-hairline">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <div className="font-grotesk text-[12px] tracking-[0.16em] uppercase text-charcoal/60">
            Under the surface
          </div>
          <h2 className="mt-4 font-display text-[32px] md:text-[44px] text-ink leading-[1.08] tracking-[-0.02em] max-w-[24ch]">
            What powers the insights.
          </h2>
          <p className="mt-5 font-grotesk text-charcoal/75 text-[17px] leading-[1.6] max-w-[60ch]">
            Five layers, each doing one job. Conversations go in. Themes and gaps come
            out. A privacy layer sits between the people who speak and the people who
            read.
          </p>
        </Reveal>


        <Reveal delay={120}>
          <div className="mt-14">
            <OperationalFlow />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TeamSection() {
  const people = [
    {
      name: 'Dean McKenna',
      role: 'Founder & Partner',
      bio: 'Dean founded IV Talent on a simple idea: small and mid-sized organizations deserve the same caliber of talent and culture expertise that the Fortune 500 takes for granted, minus the consulting jargon and the eye-watering invoice. With more than 30 years across leadership, organizational culture, executive integration, and transformation, he has worked across six continents, built executive teams in London, New York, Dubai, and São Paulo, and guided companies through cross-border mergers, post-acquisition integration, and leadership transitions where the real work happens in time zones you do not share. A Fractional CHRO and Board Advisor at heart, he is known for turning complicated people problems into practical, human-centered solutions.',
    },
    {
      name: 'Frank Niles, Ph.D.',
      role: 'Staff Psychologist',
      bio: 'Dr. Frank Niles is a business psychologist, executive coach, and trusted advisor working at the intersection of strategy, talent, and execution. Over more than two decades, he has helped leaders at Fortune 10 companies, high-growth start-ups, and venture-backed firms strengthen leadership capability, sharpen culture, and align executive teams around what matters most. A former Los Angeles County paramedic and university professor, Frank has been featured in CNN, Fast Company, Forbes, NBC, and Inc., and holds a Ph.D. from the University of Houston.',
    },
    {
      name: 'Marcelo Couto, MS',
      role: 'Senior Advisor',
      bio: "Marcelo brings 20 years of experience building high-performance cultures across five countries within a global Fortune 500 environment. His work spans leadership development, organizational culture, and global talent strategy, with a research focus on the dark side of leadership — the patterns many organizations overlook until they surface in performance, trust, or retention challenges. He holds a Master's in Management for Competitiveness – People Management from Fundação Getulio Vargas (FGV EAESP), a degree title that takes almost as long to say as it did to earn, and anchors IV Talent's work across South America.",
    },
  ];

  return (
    <section id="team" className="bg-cream py-20 md:py-28 border-t border-hairline">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <p className="font-grotesk text-[13px] text-charcoal/55 leading-relaxed max-w-[62ch]">
            The team behind IV Talent brings deep experience across leadership, culture, psychology, and transformation.
          </p>
        </Reveal>

        <div className="mt-14 md:mt-16 divide-y divide-hairline border-t border-hairline">
          {people.map((p, i) => (
            <Reveal key={p.name} delay={i * 100}>
              <div className="py-10 md:py-12 grid md:grid-cols-12 gap-6 md:gap-10">
                <div className="md:col-span-4">
                  <h3 className="font-display text-[20px] md:text-[22px] text-ink leading-[1.25] tracking-[-0.01em]">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 font-grotesk text-[12px] uppercase tracking-[0.14em] text-charcoal/50">
                    {p.role}
                  </p>
                </div>
                <div className="md:col-span-8">
                  <p className="font-grotesk text-charcoal/80 text-[16px] leading-[1.65] max-w-[66ch]">
                    {p.bio}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FoundingPartner() {
  const bullets = [
    'Always-on confidential conversations, voice or text',
    'Themes, gap map, and weekly trends across teams',
    'Aggregated-only reporting with k ≥ 5 enforced at the data layer',
    'Executive and team-lead briefings written from real employee voices',
  ];
  return (
    <section id="pricing" className="bg-cream-warm py-20 md:py-28 border-y border-hairline">
      <div className="max-w-content mx-auto px-6 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Reveal>
            <h2 className="font-display text-[32px] md:text-[44px] text-ink leading-[1.08] tracking-[-0.02em] max-w-[18ch]">
              Join our cohort
            </h2>
            <p className="mt-5 font-grotesk text-charcoal/75 text-[17px] leading-[1.6] max-w-[44ch]">
              CultureLens is being shaped with a small group of organizations who treat
              culture as information they can make decisions on. We add new partners selectively.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={120}>
            <div className="paper-card rounded-[4px] p-8 md:p-10">
              <div className="font-grotesk text-[12px] text-charcoal/60">
                Founding partner engagement
              </div>
              <div className="mt-3 flex items-baseline gap-3">
                <div className="font-display text-[40px] md:text-[48px] leading-none text-ink">
                  Call for pricing
                </div>
              </div>
              <p className="mt-4 font-grotesk text-charcoal/75 text-[15px] leading-relaxed max-w-[58ch]">
                Inclusive of the full platform: confidential conversations, theme and gap
                analysis, weekly briefings, and the privacy architecture above. Engagements
                are scoped individually to your organization.
              </p>


              <ul className="mt-7 space-y-3 border-t border-hairline pt-6">
                {bullets.map((b) => (
                  <li
                    key={b}
                    className="flex gap-3 font-grotesk text-charcoal/85 text-[15px] leading-relaxed"
                  >
                    <Check className="h-5 w-5 text-ink/70 shrink-0 mt-0.5" strokeWidth={1.75} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <GetStartedDialog>
                  <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px] font-medium">
                    Request access <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </GetStartedDialog>
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-1 font-grotesk text-[14px] text-charcoal hover:text-ink transition-colors"
                >
                  Read the privacy policy <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-ink text-cream py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <h2 className="md:col-span-8 font-display text-[32px] md:text-[44px] leading-[1.1] tracking-[-0.02em] max-w-[24ch]">
              See what your organization sounds like when it speaks freely.
            </h2>
            <div className="md:col-span-4 md:text-right">
              <GetStartedDialog>
                <Button className="bg-cream hover:bg-cream/90 text-ink rounded-full h-12 px-6 font-grotesk text-[15px] font-medium">
                  Request access <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </GetStartedDialog>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const cols = [
    {
      title: 'Product',
      links: [
        { label: 'Trust', href: '#trust' },
        { label: 'System', href: '#system' },
        // { label: 'Team', href: '#team' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'Sign in', href: '/auth/signin', internal: true },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'Who We Are', href: '/who-we-are#who-its-for', internal: true },
        { label: 'Contact', href: '/legal', internal: true },
        { label: 'Request access', href: '#', cta: true },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Legal Notice', href: '/legal', internal: true },
        { label: 'Privacy Policy', href: '/privacy', internal: true },
      ],
    },
  ];

  return (
    <footer className="bg-cream border-t border-hairline pt-20 pb-10">
      <div className="max-w-content mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-5 font-grotesk text-charcoal/70 max-w-xs leading-relaxed">
              Culture intelligence for organizations that take their people seriously.
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.title} className="md:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/50 mb-4">
                {c.title}
              </div>
              <ul className="space-y-3">
                {c.links.map((l: any) =>
                  l.cta ? (
                    <li key={l.label}>
                      <GetStartedDialog>
                        <button className="font-grotesk text-charcoal hover:text-ink transition-colors text-left">
                          {l.label}
                        </button>
                      </GetStartedDialog>
                    </li>
                  ) : l.internal ? (
                    <li key={l.label}>
                      <Link
                        to={l.href}
                        className="font-grotesk text-charcoal hover:text-ink transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ) : (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="font-grotesk text-charcoal hover:text-ink transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-hairline pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs font-grotesk text-charcoal/55">
            © {new Date().getFullYear()} CultureLens by IV Talent. All rights reserved.
          </p>
          <p className="text-xs font-grotesk text-charcoal/55">
            Confidential by construction · k ≥ 5 aggregation floor
          </p>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------- */
/*  Page                                                          */
/* -------------------------------------------------------------- */

export default function Index() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div className="bg-cream text-ink font-grotesk antialiased">
      <SiteHeader />
      <main>
        <Hero />
        <ProductProofOne />
        <ProductProofTwo />
        <ValueGrid />
        <TrustArchitecture />
        <System />
        {/* <TeamSection /> */}
        <FoundingPartner />
        <FinalCTA />
      </main>

      <SiteFooter />
    </div>
  );
}
